import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Plus, 
  Loader2, 
  FileText, 
  Clock, 
  DollarSign, 
  Pencil, 
  Trash2,
  CheckCircle2,
  XCircle,
  Copy,
  BookOpen,
  Package,
} from 'lucide-react';
import { CreateEngagementTypeDialog } from './CreateEngagementTypeDialog';
import { BaselineTemplatesDialog } from './BaselineTemplatesDialog';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface EngagementCatalogProps {
  firmId: string;
  accessToken: string;
  /** Read the catalog without editing it: Sell › Catalog. Edits live in Firm › Settings (D4, the Firm-managed tier). */
  readOnly?: boolean;
}

interface EngagementType {
  id: string;
  name: string;
  description: string;
  category: string;
  defaultScope: string;
  defaultDeliverables: string[];
  standardDurationWeeks: number;
  estimatedHours: number;
  pricingModel: string;
  defaultHourlyRate?: number;
  defaultFixedPrice?: number;
  assumptions: string[];
  exclusions: string[];
  isActive: boolean;
  createdAt: string;
}

interface SOWTemplate {
  id: string;
  name: string;
  engagementTypeId?: string;
  engagementTypeName?: string;
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  migration: { label: 'Migration', color: 'bg-blue-100 text-blue-700' },
  implementation: { label: 'Implementation', color: 'bg-green-100 text-green-700' },
  fast_track: { label: 'Fast Track', color: 'bg-purple-100 text-purple-700' },
  health_checkup: { label: 'Health Checkup', color: 'bg-orange-100 text-orange-700' },
  custom: { label: 'Custom', color: 'bg-gray-100 text-gray-700' },
};

export function EngagementCatalog({ firmId, accessToken, readOnly = false }: EngagementCatalogProps) {
  const [loading, setLoading] = useState(true);
  const [engagementTypes, setEngagementTypes] = useState<EngagementType[]>([]);
  const [sowTemplates, setSOWTemplates] = useState<SOWTemplate[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<EngagementType | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [baselineTemplatesDialogOpen, setBaselineTemplatesDialogOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  useEffect(() => {
    loadEngagementTypes();
    loadSOWTemplates();
  }, [firmId]);

  const loadEngagementTypes = async () => {
    try {
      setLoading(true);
      console.log('[EngagementCatalog] Loading engagement types for firm:', firmId);
      
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[EngagementCatalog] Loaded engagement types:', {
          count: data.engagementTypes?.length || 0,
          types: data.engagementTypes,
        });
        setEngagementTypes(data.engagementTypes || []);
      } else {
        const errorText = await response.text();
        console.error('[EngagementCatalog] Failed to load engagement types:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
        });
        toast.error('Failed to load engagement types');
      }
    } catch (error) {
      console.error('[EngagementCatalog] Error loading engagement types:', error);
      toast.error('Error loading engagement types');
    } finally {
      setLoading(false);
    }
  };

  const loadSOWTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/sow-templates`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSOWTemplates(data.templates || []);
      } else {
        console.error('Failed to load SOW templates');
      }
    } catch (error) {
      console.error('Error loading SOW templates:', error);
    }
  };

  const handleDelete = async (typeId: string) => {
    if (!confirm('Are you sure you want to delete this engagement type?')) {
      return;
    }

    try {
      setDeletingId(typeId);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types/${typeId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Engagement type deleted');
        loadEngagementTypes();
      } else {
        toast.error('Failed to delete engagement type');
      }
    } catch (error) {
      console.error('Error deleting engagement type:', error);
      toast.error('Error deleting engagement type');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEdit = (type: EngagementType) => {
    setEditingType(type);
    setCreateDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCreateDialogOpen(false);
    setEditingType(null);
  };

  const handleDuplicate = async (type: EngagementType) => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/firms/${firmId}/engagement-types/${type.id}/duplicate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Engagement type duplicated');
        loadEngagementTypes();
      } else {
        toast.error('Failed to duplicate engagement type');
      }
    } catch (error) {
      console.error('Error duplicating engagement type:', error);
      toast.error('Error duplicating engagement type');
    }
  };

  const formatPrice = (pricingModel: string, hourlyRate?: number, fixedPrice?: number) => {
    if (pricingModel === 'hourly') {
      return `$${hourlyRate || 0}/hr`;
    } else if (pricingModel === 'fixed') {
      return `$${(fixedPrice || 0).toLocaleString()} fixed`;
    } else {
      return `$${hourlyRate || 0}/hr (cap: $${(fixedPrice || 0).toLocaleString()})`;
    }
  };

  const loadDebugInfo = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/debug/engagement-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[EngagementCatalog] DEBUG INFO:', data);
        setDebugInfo(data);
        toast.success(`Found ${data.count} engagement type(s) in KV store - check console`);
      } else {
        toast.error('Failed to load debug info');
      }
    } catch (error) {
      console.error('Error loading debug info:', error);
      toast.error('Error loading debug info');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="size-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Engagement Catalog</CardTitle>
              <CardDescription>
                Your firm's service offerings and engagement templates
              </CardDescription>
            </div>
            {readOnly ? (
              <p className="text-sm text-muted-foreground" data-testid="catalog-readonly-note">Edited in Firm › Settings › Engagement catalog.</p>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setBaselineTemplatesDialogOpen(true)}>
                  <BookOpen className="size-4 mr-2" />
                  Browse Templates
                </Button>
                <Button onClick={() => setCreateDialogOpen(true)}>
                  <Plus className="size-4 mr-2" />
                  New Engagement Type
                </Button>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {engagementTypes.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="size-12 mx-auto mb-3 text-gray-400" />
              <h3 className="font-semibold mb-2">No engagement types yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first engagement type to start building your service catalog
              </p>
              {!readOnly && (
                <div className="flex gap-2 justify-center">
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="size-4 mr-2" />
                    Create Engagement Type
                  </Button>
                  <Button variant="outline" onClick={loadDebugInfo}>
                    Debug KV Store
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {engagementTypes.filter(type => type != null).map((type) => {
                // Handle missing or null category gracefully
                const categoryInfo = type.category && CATEGORY_LABELS[type.category] 
                  ? CATEGORY_LABELS[type.category] 
                  : CATEGORY_LABELS.custom;
                
                // Find linked templates
                const linkedTemplates = sowTemplates.filter(template => template.engagementTypeId === type.id);
                
                return (
                  <Card key={type.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold">{type.name}</h3>
                            <Badge className={categoryInfo.color}>
                              {categoryInfo.label}
                            </Badge>
                            {type.isActive ? (
                              <CheckCircle2 className="size-4 text-green-600" />
                            ) : (
                              <XCircle className="size-4 text-gray-400" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                        {!readOnly && <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(type)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(type.id)}
                            disabled={deletingId === type.id}
                          >
                            {deletingId === type.id ? (
                              <Loader2 className="size-4 animate-spin" />
                            ) : (
                              <Trash2 className="size-4 text-red-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(type)}
                          >
                            <Copy className="size-4" />
                          </Button>
                        </div>}
                      </div>

                      {/* Key metrics */}
                      <div className="grid grid-cols-3 gap-3 mb-4 pb-4 border-b">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                            <Clock className="size-3" />
                            Duration
                          </div>
                          <div className="font-semibold">
                            {type.standardDurationWeeks || 'TBD'} {type.standardDurationWeeks === 1 ? 'week' : 'weeks'}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                            <FileText className="size-3" />
                            Hours
                          </div>
                          <div className="font-semibold">
                            {type.estimatedHours || 'TBD'}h
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                            <DollarSign className="size-3" />
                            Pricing
                          </div>
                          <div className="font-semibold text-xs">
                            {formatPrice(type.pricingModel, type.defaultHourlyRate, type.defaultFixedPrice)}
                          </div>
                        </div>
                      </div>

                      {/* Linked Templates */}
                      {linkedTemplates.length > 0 && (
                        <div className="mb-3">
                          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-2">
                            <Package className="size-3 text-purple-500" />
                            SOW Templates ({linkedTemplates.length})
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {linkedTemplates.slice(0, 2).map((template) => (
                              <Badge key={template.id} variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                {template.name}
                              </Badge>
                            ))}
                            {linkedTemplates.length > 2 && (
                              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                                +{linkedTemplates.length - 2} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Deliverables */}
                      {type.defaultDeliverables && type.defaultDeliverables.length > 0 && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-muted-foreground mb-2">
                            Deliverables ({type.defaultDeliverables.length})
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {type.defaultDeliverables.slice(0, 3).map((deliverable, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {deliverable}
                              </Badge>
                            ))}
                            {type.defaultDeliverables.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{type.defaultDeliverables.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Scope preview */}
                      {type.defaultScope && (
                        <div>
                          <div className="text-xs font-medium text-muted-foreground mb-1">
                            Scope Preview
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {type.defaultScope}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <CreateEngagementTypeDialog
        open={createDialogOpen}
        onOpenChange={handleDialogClose}
        firmId={firmId}
        accessToken={accessToken}
        onCreated={() => {
          loadEngagementTypes();
          handleDialogClose();
        }}
        editingType={editingType}
      />

      {/* Baseline Templates Dialog */}
      <BaselineTemplatesDialog
        open={baselineTemplatesDialogOpen}
        onOpenChange={() => setBaselineTemplatesDialogOpen(false)}
        firmId={firmId}
        accessToken={accessToken}
        onCreated={() => {
          loadEngagementTypes();
          setBaselineTemplatesDialogOpen(false);
        }}
      />
    </>
  );
}